import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ProviderCard from "./ProviderCard";
import {
  getProviders,
  getUserFavouriteProviderIds,
  toggleProviderFavourite,
} from "./utils/providerNetworkApi";
import "../../../css/ProviderNetwork/ProviderNetwork.css";
import Sidebar from "../../DesignSystem/Sidebar";
import { Helmet } from "react-helmet-async";
import PrimaryButton from "../../Marketing/Approvals/buttons/PrimaryButton";
import SearchField from "../../DesignSystem/Inputs/SearchField";
import SecondaryButton from "../../DesignSystem/Buttons/SecondaryButton";
import { useUserEmail } from "../../SignIn/UserEmailContext";
import { ReactComponent as BookmarkIcon } from "../../../assets/icons/Bookmark.svg";
import { ReactComponent as BookmarkFilledIcon } from "../../../assets/icons/Bookmark (filled).svg";
import { useUserPermissions } from "../../SignIn/UserPermissionsContext";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";

const PROVIDER_SCROLL_KEY = "providerNetworkScrollTop";
const PROVIDER_SEARCH_KEY = "providerNetworkSearch";
const PROVIDER_SHOULD_RESTORE_SCROLL_KEY = "providerNetworkShouldRestoreScroll";

const ProviderNetworkPage = () => {
  const navigate = useNavigate();
  const { userEmail } = useUserEmail();
  const mainContainerRef = useRef(null);
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  const [providers, setProviders] = useState([]);
  const [search, setSearch] = useState(() => {
    return sessionStorage.getItem(PROVIDER_SEARCH_KEY) || "";
  });
  const [loading, setLoading] = useState(true);
  const { userPermissions } = useUserPermissions();
  const isAdmin = userPermissions.includes("Administrator");
  const handleSearchChange = (e) => {
    const value = e.target.value;

    setSearch(value);
    sessionStorage.setItem(PROVIDER_SEARCH_KEY, value);
    sessionStorage.removeItem(PROVIDER_SHOULD_RESTORE_SCROLL_KEY);

    if (mainContainerRef.current) {
      mainContainerRef.current.scrollTop = 0;
    }
  };

  const fetchProviders = async () => {
    setLoading(true);

    try {
      const [providerData, favouriteIds] = await Promise.all([
        getProviders(),
        getUserFavouriteProviderIds(userEmail),
      ]);

      const favouriteSet = new Set(favouriteIds);

      // Log only the totals
      // console.log("Total providers:", providerData.length);
      const totalRecords = providerData.reduce(
        (sum, p) => sum + (Number(p.planCount) || 0),
        0,
      );
      // console.log("Total records:", totalRecords);

      setProviders(
        providerData.map((provider) => ({
          ...provider,
          isFavourite: favouriteSet.has(provider.id),
        })),
      );
    } catch (err) {
      // console.error(err);
      alert(err.message || "Failed to load providers.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchProviders();
  }, []);

  const filteredProviders = useMemo(() => {
    const term = search.trim().toLowerCase();

    return providers.filter((provider) => {
      const matchesSearch =
        !term ||
        provider.providerName?.toLowerCase().includes(term) ||
        provider.providerNameLower?.includes(term) ||
        (provider.plans &&
          provider.plans.some(
            (plan) =>
              plan.planName?.toLowerCase().includes(term) ||
              plan.planNameLower?.includes(term),
          ));

      const matchesBookmark = !showBookmarkedOnly || provider.isFavourite;

      return matchesSearch && matchesBookmark;
    });
  }, [providers, search, showBookmarkedOnly]);

  useEffect(() => {
    if (loading) return;

    const shouldRestore = sessionStorage.getItem(
      PROVIDER_SHOULD_RESTORE_SCROLL_KEY,
    );

    if (shouldRestore !== "true") return;

    const savedScrollTop = sessionStorage.getItem(PROVIDER_SCROLL_KEY);

    if (!savedScrollTop) return;

    const restoreScroll = () => {
      const container = mainContainerRef.current;

      if (!container) return;

      container.scrollTop = Number(savedScrollTop);
    };

    requestAnimationFrame(() => {
      restoreScroll();

      setTimeout(restoreScroll, 100);
      setTimeout(() => {
        restoreScroll();

        sessionStorage.removeItem(PROVIDER_SHOULD_RESTORE_SCROLL_KEY);
      }, 300);
    });
  }, [loading]);

  const handleToggleFavourite = async (provider) => {
    try {
      await toggleProviderFavourite(
        provider.id,
        provider.isFavourite,
        userEmail,
      );

      setProviders((prev) =>
        prev.map((item) =>
          item.id === provider.id
            ? {
                ...item,
                isFavourite: !item.isFavourite,
              }
            : item,
        ),
      );
    } catch (err) {
      // console.error(err);
      alert(err.message || "Failed to update bookmark.");
    }
  };

  const handleOpenProvider = (providerId) => {
    const scrollTop = mainContainerRef.current?.scrollTop || 0;

    sessionStorage.setItem(PROVIDER_SCROLL_KEY, String(scrollTop));
    sessionStorage.setItem(PROVIDER_SEARCH_KEY, search);
    sessionStorage.setItem(PROVIDER_SHOULD_RESTORE_SCROLL_KEY, "true");

    navigate(`/provider-index/${providerId}`);
  };

  // Calculate totals for display
  const totalProviders = providers.length;
  const totalRecords = providers.reduce(
    (sum, p) => sum + (Number(p.planCount) || 0),
    0,
  );

  return (
    <div className="main-content">
      <Helmet>
        <title>Provider Index</title>
      </Helmet>

      <Sidebar />
      <div className="Main-Container" ref={mainContainerRef}>
        <div className="provider-network-page">
          <Breadcrumb
            items={[
              {
                label: "Compliance",
                to: "/Department/Compliance",
              },
              { label: "Provider Index" },
            ]}
          />
          <div className="provider-network-header">
            <div>
              <div className="FP_H3 provider-network-title">Provider Index</div>
              <span className="FP_Help_Text"></span>
              <div className="FP_Content black">
                {totalProviders} providers • {totalRecords} records
                {/* Browse through the directory for financial service providers. */}
              </div>
            </div>

            <div className="provider-network-actions">
              <button
                type="button"
                className={`provider-network-bookmark-filter ${
                  showBookmarkedOnly ? "is-active" : ""
                }`}
                onClick={() => {
                  sessionStorage.removeItem(PROVIDER_SHOULD_RESTORE_SCROLL_KEY);
                  setShowBookmarkedOnly((prev) => !prev);

                  if (mainContainerRef.current) {
                    mainContainerRef.current.scrollTop = 0;
                  }
                }}
              >
                {showBookmarkedOnly ? <BookmarkFilledIcon /> : <BookmarkIcon />}
              </button>
              <SearchField
                value={search}
                placeholder="Provider name/Plan name"
                onChange={handleSearchChange}
              />
              {isAdmin && (
                <SecondaryButton
                  type="button"
                  label="View Requests"
                  onClick={() => navigate("/provider-index/issue-reports")}
                />
              )}

              <SecondaryButton
                type="button"
                label="Add Provider"
                onClick={() => navigate("/provider-index/add")}
              >
                {/* Add Provider */}
              </SecondaryButton>
            </div>
          </div>

          {loading ? (
            <div className="provider-network-empty">Loading providers...</div>
          ) : filteredProviders.length ? (
            <div className="provider-network-grid">
              {filteredProviders.map((provider) => (
                <ProviderCard
                  key={provider.id}
                  provider={provider}
                  onClick={() => handleOpenProvider(provider.id)}
                  onToggleFavourite={() => handleToggleFavourite(provider)}
                />
              ))}
            </div>
          ) : (
            <div className="provider-network-empty">
              {showBookmarkedOnly
                ? "No bookmarked providers found."
                : "No providers found."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProviderNetworkPage;
